import { z } from 'zod';
import { equipmentValidationPatterns, equipmentValidationMessages, getLegTypeConfig, } from './constants';
// Equipment item shape (loose — conditional rules applied via superRefine)
const equipmentSchema = z.object({
    assetType: z.enum(['container', 'chassis', 'trailer']).or(z.literal('')),
    equipmentNumber: z.string(),
});
// Base shape before conditional rules
const addLegBaseSchema = z.object({
    legType: z.string().min(1, 'Leg type is required'),
    equipment: z.array(equipmentSchema),
    totalWeight: z.string().optional(),
    weightUnit: z.string().optional(),
    pickupName: z.string().optional(),
    pickupAddress: z.string().min(1, 'Pickup address is required'),
    deliverName: z.string().optional(),
    deliverAddress: z.string().min(1, 'Delivery address is required'),
    carrier: z.string().optional(),
    specialInstructions: z.string().optional(),
});
// Add Leg form schema — equipment rules are conditional on leg type
export const addLegSchema = addLegBaseSchema
    .superRefine((data, ctx) => {
    var _a;
    const requiresEquipment = ((_a = getLegTypeConfig(data.legType)) === null || _a === void 0 ? void 0 : _a.requiresEquipment) !== false;
    if (!requiresEquipment)
        return;
    // Must have at least one equipment entry
    if (data.equipment.length === 0) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'At least one equipment is required',
            path: ['equipment'],
        });
        return;
    }
    // Each equipment row must have a valid number
    data.equipment.forEach((eq, i) => {
        const trimmed = (eq.equipmentNumber || '').trim();
        if (trimmed.length === 0) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'Equipment number is required',
                path: ['equipment', i, 'equipmentNumber'],
            });
        }
    });
})
    .transform((data) => {
    var _a;
    const requiresEquipment = ((_a = getLegTypeConfig(data.legType)) === null || _a === void 0 ? void 0 : _a.requiresEquipment) !== false;
    if (requiresEquipment)
        return data;
    // Strip empty equipment rows for leg types that don't require equipment
    return Object.assign(Object.assign({}, data), { equipment: data.equipment.filter((eq) => eq.equipmentNumber && eq.equipmentNumber.trim().length > 0) });
});
// Add Hub form schema
const requiredString = (message) => z.preprocess((val) => (typeof val === 'string' ? val : ''), z.string().trim().min(1, message));
export const addHubSchema = z.object({
    hubbingType: z.enum(['pre_pull', 'stop_off', 'yard_diversion']),
    street: requiredString('Street address is required'),
    city: requiredString('City is required'),
    state: requiredString('State is required'),
    zip: requiredString('Zip code is required'),
    address: z.string().optional(),
    specialInstructions: z.string().optional(),
    // Optional name when a custom yard name is set
    name: z.string().optional(),
    // yardId is optional now and not required by validation
    yardId: z.string().optional(),
});
// Helper function to validate equipment format and return warnings
export function validateEquipmentFormat(equipmentNumber, assetType) {
    if (!equipmentNumber)
        return null;
    const typeKey = (assetType || '').toLowerCase();
    const pattern = equipmentValidationPatterns[typeKey];
    const message = equipmentValidationMessages[typeKey];
    if (!pattern)
        return null;
    if (!pattern.test(equipmentNumber)) {
        return message;
    }
    return null;
}
